import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchBlogs, imgUrl } from "../api";

function BlogCard({ blog, featured }) {
  const navigate = useNavigate();
  const imgSrc = blog.heroImg ? imgUrl(blog.heroImg) : "/images/about_us.jpg";
  const date = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  if (featured) {
    return (
      <div
        onClick={() => navigate(`/blogs/${blog.slug || blog._id}`)}
        className="relative overflow-hidden rounded-br-[40px] h-full group cursor-pointer"
        style={{ minHeight: "500px" }}
      >
        <img
          src={imgSrc}
          alt={blog.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          {date && (
            <p className="font-body text-white/50 text-[10px] tracking-widest uppercase mb-2">
              {date}
            </p>
          )}
          <p className="font-display text-white text-xl md:text-2xl font-light mb-3 leading-snug">
            {blog.title}
          </p>
          {blog.excerpt && (
            <p className="font-body text-white/65 text-sm leading-relaxed mb-5 line-clamp-3">
              {blog.excerpt}
            </p>
          )}
          <button className="font-body text-white text-[11px] tracking-[0.15em] uppercase flex items-center gap-2 group/btn">
            <span className="group-hover/btn:text-gold transition-colors duration-200">
              Read More
            </span>
            <span className="w-4 h-[1px] bg-white group-hover/btn:bg-gold group-hover/btn:w-6 transition-all duration-300" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => navigate(`/blogs/${blog.slug || blog._id}`)}
      className="relative overflow-hidden rounded-br-[40px] group cursor-pointer"
      style={{ minHeight: "240px" }}
    >
      <img
        src={imgSrc}
        alt={blog.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        {date && (
          <p className="font-body text-white/50 text-[10px] tracking-widest uppercase mb-2">
            {date}
          </p>
        )}
        <p className="font-display text-white text-lg md:text-xl font-light mb-2 leading-snug">
          {blog.title}
        </p>
        {blog.excerpt && (
          <p className="font-body text-white/60 text-xs md:text-sm leading-relaxed mb-4 line-clamp-2">
            {blog.excerpt}
          </p>
        )}
        <button className="font-body text-white text-[11px] tracking-[0.15em] uppercase flex items-center gap-2 group/btn">
          <span className="group-hover/btn:text-gold transition-colors duration-200">
            Read More
          </span>
          <span className="w-4 h-[1px] bg-white group-hover/btn:bg-gold group-hover/btn:w-6 transition-all duration-300" />
        </button>
      </div>
    </div>
  );
}

// Skeleton
function BlogSkeleton({ featured }) {
  return (
    <div
      className={`rounded-br-[40px] bg-stone-200 animate-pulse ${featured ? "min-h-[500px]" : "min-h-[240px]"}`}
    />
  );
}

export default function BlogsNews({ title = "Blogs & News", showTag = true }) {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try featured first, fall back to latest 3
    fetchBlogs({ featured: true, limit: 3 })
      .then((d) => {
        const featured = d.data || [];
        if (featured.length >= 3) {
          setBlogs(featured);
        } else {
          // Not enough featured, get latest 3
          return fetchBlogs({ limit: 3 }).then((d2) => setBlogs(d2.data || []));
        }
      })
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="blogs"
      className="bg-cream py-14 md:py-20 px-4 sm:px-8 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 md:mb-10">
          {showTag && (
            <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
              Blogs
            </span>
          )}
          <div className="flex items-end justify-between">
            <h2
              className="font-display font-normal text-stone-900 leading-tight"
              style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}
            >
              {title}
            </h2>
            <button
              onClick={() => {
                window.scrollTo({ top: 0 });
                navigate("/all-blogs");
              }}
              className="hidden sm:flex items-center gap-2 font-body text-[11px] tracking-[0.12em] uppercase text-stone-600 hover:text-gold transition-colors duration-200 group"
            >
              View All
              <span className="w-6 h-6 rounded-full bg-gold group-hover:bg-stone-900 flex items-center justify-center text-white text-xs transition-colors duration-300">
                →
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:row-span-2">
            {loading ? (
              <BlogSkeleton featured={true} />
            ) : blogs[0] ? (
              <BlogCard blog={blogs[0]} featured={true} />
            ) : null}
          </div>
          <div>
            {loading ? (
              <BlogSkeleton featured={false} />
            ) : blogs[1] ? (
              <BlogCard blog={blogs[1]} featured={false} />
            ) : null}
          </div>
          <div>
            {loading ? (
              <BlogSkeleton featured={false} />
            ) : blogs[2] ? (
              <BlogCard blog={blogs[2]} featured={false} />
            ) : null}
          </div>
        </div>

        <div className="flex justify-center mt-8 sm:hidden">
          <button
            onClick={() => {
              window.scrollTo({ top: 0 });
              navigate("/all-blogs");
            }}
            className="flex items-center gap-2 font-body text-[11px] tracking-[0.15em] uppercase text-stone-600 hover:text-gold transition-colors duration-200 group"
          >
            View All Posts
            <span className="w-6 h-6 rounded-full bg-gold group-hover:bg-stone-900 flex items-center justify-center text-white text-xs transition-colors duration-300">
              →
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
